import { useState } from "react";
import {
  Banner,
  Button,
  Chip,
  Dialog,
  FieldGroup,
  TextField,
} from "@jkast90/kast-common/ui";
import { MentionInput } from "../ui";
import { photosApi, promptsApi, type Memory } from "../../services/kindlyApi";
import UserSelector from "../../../profile/components/UserSelector";
import { useMemoriesChanged, NO_PROFILES, useProfiles } from "../../queries";
import PhotoUpload, { PhotoData } from "../PhotoUpload";
import { banners, useBanner } from "@common/banner";
import styles from "./CreateMemoryDialog.module.css";

const FORM_ID = "memory-form";
import { errorMessage } from "@jkast90/kast-common/format";

interface MemoryData {
  id?: string;
  title: string;
  description: string;
  date?: string;
  tagged_users: number[];
  suggestions?: string;
  tags?: string[];
  images?: string[];
}

interface CreateMemoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: MemoryData) => void;
  memory?: Partial<Memory> | null; // For editing existing memory
  suggestionPrompt?: string; // For pre-filling from suggestions
}

export default function CreateMemoryDialog({
  open,
  onClose,
  onSave,
  memory,
  suggestionPrompt,
}: CreateMemoryDialogProps) {
  const { data: profiles = NO_PROFILES } = useProfiles();
  const memoriesChanged = useMemoriesChanged();
  const { showSuccess, showError } = useBanner();
  const [formData, setFormData] = useState<MemoryData>({
    title: "",
    description: "",
    date: new Date().toISOString().split("T")[0], // Default to today
    tagged_users: [],
    suggestions: "",
    tags: [],
    images: [],
  });

  const [tagInput, setTagInput] = useState("");
  const [loadingPrompts, setLoadingPrompts] = useState(false);
  const [generatedPrompts, setGeneratedPrompts] = useState<string[]>([]);
  const [showPrompts, setShowPrompts] = useState(false);

  // Extract mentioned user IDs from text
  const extractMentionedUserIds = (text: string): number[] => {
    const mentionPattern = /@(\w+)/g;
    const matches = text.matchAll(mentionPattern);
    const userIds: number[] = [];

    for (const match of matches) {
      const firstName = match[1];
      const profile = profiles.find(
        (p) => p.first_name.toLowerCase() === firstName.toLowerCase(),
      );
      if (profile && !userIds.includes(profile.id)) {
        userIds.push(profile.id);
      }
    }

    return userIds;
  };

  // Populate the form whenever the dialog opens or its inputs change, during
  // render rather than in an effect so it never paints with stale values.
  const resetInputs = [open, memory, suggestionPrompt, profiles] as const;
  const [prevInputs, setPrevInputs] = useState<typeof resetInputs | null>(null);
  if (!prevInputs || resetInputs.some((v, i) => v !== prevInputs[i])) {
    setPrevInputs(resetInputs);
    if (open) {
      // Populate form with memory data when editing, otherwise reset
      if (memory) {
        // Extract mentioned user IDs from all text fields
        const titleMentions = extractMentionedUserIds(memory.title || "");
        const descriptionMentions = extractMentionedUserIds(
          memory.description || "",
        );
        const suggestionsMentions = extractMentionedUserIds(
          memory.suggestions || "",
        );

        // Combine all mentions with existing tagged_users
        const allMentionedUserIds = [
          ...(memory.tagged_users || []),
          ...titleMentions,
          ...descriptionMentions,
          ...suggestionsMentions,
        ];

        // Remove duplicates
        const uniqueTaggedUsers = [...new Set(allMentionedUserIds)];

        setFormData({
          title: memory.title || "",
          description: memory.description || "",
          date: memory.date || new Date().toISOString().split("T")[0],
          tagged_users: uniqueTaggedUsers,
          suggestions: memory.suggestions || "",
          tags: memory.tags || [],
          images: memory.images || [],
        });
      } else {
        // For new memories, check if suggestionPrompt has mentions
        const promptMentions = suggestionPrompt
          ? extractMentionedUserIds(suggestionPrompt)
          : [];

        setFormData({
          title: suggestionPrompt || "",
          description: "",
          date: new Date().toISOString().split("T")[0],
          tagged_users: promptMentions,
          suggestions: "",
          tags: [],
          images: [],
        });
      }
      setTagInput("");
    }
  }

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleTitleChange = (value: string, mentionedUserIds?: number[]) => {
    setFormData((prev) => {
      const currentTagged = prev.tagged_users || [];
      const newTagged = mentionedUserIds
        ? [...new Set([...currentTagged, ...mentionedUserIds])]
        : currentTagged;
      return {
        ...prev,
        title: value,
        tagged_users: newTagged,
      };
    });
  };

  const handleDescriptionChange = (
    value: string,
    mentionedUserIds?: number[],
  ) => {
    setFormData((prev) => {
      const currentTagged = prev.tagged_users || [];
      const newTagged = mentionedUserIds
        ? [...new Set([...currentTagged, ...mentionedUserIds])]
        : currentTagged;
      return {
        ...prev,
        description: value,
        tagged_users: newTagged,
      };
    });
  };

  const handleSuggestionsChange = (
    value: string,
    mentionedUserIds?: number[],
  ) => {
    setFormData((prev) => {
      const currentTagged = prev.tagged_users || [];
      const newTagged = mentionedUserIds
        ? [...new Set([...currentTagged, ...mentionedUserIds])]
        : currentTagged;
      return {
        ...prev,
        suggestions: value,
        tagged_users: newTagged,
      };
    });
  };

  const handleTaggedUsersChange = (selectedIds: number[]) => {
    setFormData((prev) => ({
      ...prev,
      tagged_users: selectedIds,
    }));
  };

  const handleAddTag = () => {
    const tag = tagInput.trim().toLowerCase();
    if (tag && !formData.tags?.includes(tag)) {
      setFormData((prev) => ({
        ...prev,
        tags: [...(prev.tags || []), tag],
      }));
      setTagInput("");
    }
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setFormData((prev) => ({
      ...prev,
      tags: prev.tags?.filter((t) => t !== tagToRemove) || [],
    }));
  };

  const handleTagInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleSubmit = (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    if (!formData.title.trim() || !formData.description.trim()) {
      return;
    }
    onSave(formData);
  };

  const handlePhotoUpload = async (photoData: PhotoData) => {
    // If editing an existing memory, add photo directly to the memory
    if (memory?.id) {
      try {
        await photosApi.addToMemory(Number(memory.id), photoData.s3_key);
        setFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), photoData.s3_key],
        }));
        // Refetch all memories to update the list with the new image
        await memoriesChanged();
        showSuccess("Photo added to memory");
      } catch (error: unknown) {
        console.error("Error adding photo to memory:", error);
        showError(errorMessage(error, "Failed to add photo to memory"));
      }
    } else {
      // For new memories, just add to local state
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), photoData.s3_key],
      }));
    }
  };

  const handlePhotoError = (error: string) => {
    showError(error);
  };

  const handleRemoveImage = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || [],
    }));
  };

  const handleGetPrompts = async () => {
    if (!formData.title.trim() && !formData.description.trim()) {
      showError("Please add a title or description first to generate prompts.");
      return;
    }

    setLoadingPrompts(true);
    try {
      const { prompts } = await promptsApi.generatePrompts(
        "memory",
        formData.title || undefined,
        formData.description || undefined,
      );
      setGeneratedPrompts(prompts || []);
      setShowPrompts(true);
    } catch (error: unknown) {
      console.error("Failed to generate prompts:", error);
      showError("Failed to generate prompts. Please try again.");
    } finally {
      setLoadingPrompts(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      size="lg"
      title={memory ? "Edit Memory" : "Create Memory"}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" type="submit" form={FORM_ID}>
            {memory ? "Save Changes" : "Create Memory"}
          </Button>
        </>
      }
    >
      <Banner store={banners} />

      <form id={FORM_ID} onSubmit={handleSubmit} className="kui-form">
        <FieldGroup label="Title *">
          <MentionInput
            value={formData.title}
            onChange={handleTitleChange}
            profiles={profiles}
            placeholder="e.g. Summer Camping Trip 2024 with @"
            rows={1}
          />
        </FieldGroup>

        <TextField
          label="Date"
          type="date"
          name="date"
          value={formData.date || ""}
          onChange={handleChange}
        />

        <FieldGroup label="Description *">
          <MentionInput
            value={formData.description}
            onChange={handleDescriptionChange}
            profiles={profiles}
            placeholder="Share the story of this memory... Use @ to mention family members"
            rows={4}
          />
        </FieldGroup>

        <div className={styles.fieldFullWidth}>
          <Button
            onClick={handleGetPrompts}
            loading={loadingPrompts}
            loadingLabel="Generating questions..."
            className={styles.getPromptsButton}
          >
            💡 Get Prompts
          </Button>
          {showPrompts && generatedPrompts.length > 0 && (
            <div className={styles.promptsContainer}>
              <h3 className={styles.promptsTitle}>
                Questions to help develop this memory:
              </h3>
              <ul className="kui-list-reset">
                {generatedPrompts.map((prompt, index) => (
                  <li key={index} className={styles.promptItem}>
                    {prompt}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>

        <FieldGroup label="Suggestions (optional)">
          <MentionInput
            value={formData.suggestions || ""}
            onChange={handleSuggestionsChange}
            profiles={profiles}
            placeholder="Ideas for next time or related suggestions... Use @ to mention family members"
            rows={2}
          />
        </FieldGroup>

        <div className={styles.fieldFullWidth}>
          <div className={styles.tagInputContainer}>
            <TextField
              label="Info Tags (optional)"
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={handleTagInputKeyDown}
              placeholder="e.g. vacation, birthday, holiday"
              wrapperClassName={styles.tagField}
            />
            <Button
              size="sm"
              onClick={handleAddTag}
              className={styles.addTagButton}
            >
              Add Tag
            </Button>
          </div>
          {formData.tags && formData.tags.length > 0 && (
            <div className={styles.tagsList}>
              {formData.tags.map((tag) => (
                <Chip key={tag} onRemove={() => handleRemoveTag(tag)}>
                  {tag}
                </Chip>
              ))}
            </div>
          )}
        </div>

        <div className={styles.fieldFullWidth}>
          <UserSelector
            selectedUserIds={formData.tagged_users}
            onChange={handleTaggedUsersChange}
            label="Tag Family Members"
          />
        </div>

        <FieldGroup label="Photos (optional)">
          <PhotoUpload
            folder="memories"
            onUploadComplete={handlePhotoUpload}
            onUploadError={handlePhotoError}
            buttonText="Add Photo"
            showPreview={false}
          />
          {formData.images && formData.images.length > 0 && (
            <div className={styles.imagesList}>
              {formData.images.map((_s3Key, index) => (
                <div key={index} className={styles.imageItem}>
                  <span className={styles.imageName}>Photo {index + 1}</span>
                  <Button
                    size="sm"
                    onClick={() => handleRemoveImage(index)}
                    className={styles.removeImageButton}
                  >
                    Remove
                  </Button>
                </div>
              ))}
            </div>
          )}
        </FieldGroup>
      </form>
    </Dialog>
  );
}
